import '../shared.ts';
import './index.css';

import { animatePanorama } from '../../utils/animation.ts';
import '../../web-components/pan-oh';
import type { PanOh } from '../../web-components/pan-oh';

interface PageContext {
    page: {
        availableSrc?: string;
        lonMin?: number;
        lonMax?: number;
        latMin?: number;
        latMax?: number;
        cropLeft?: number;
        cropRight?: number;
        cropTop?: number;
        cropBottom?: number;
        backgroundDarken?: number;
        backgroundDesaturate?: number;
    };
}

function setupDebugConsole() {
    const debugDiv = document.createElement('div');
    debugDiv.id = 'debug-console';
    debugDiv.style.cssText = `
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        max-height: 200px;
        overflow-y: auto;
        background: rgba(0, 0, 0, 0.9);
        color: #fff;
        font-family: monospace;
        font-size: 11px;
        padding: 8px;
        z-index: 10000;
        border-top: 2px solid #333;
    `;
    document.body.appendChild(debugDiv);

    const addMessage = (type: 'log' | 'error' | 'warn', ...args: unknown[]) => {
        const message = document.createElement('div');
        message.style.cssText = `
            padding: 2px 0;
            color: ${type === 'error' ? '#ef4444' : type === 'warn' ? '#f59e0b' : '#10b981'};
        `;
        message.textContent = `[${type}] ${args.map(arg =>
            typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
        ).join(' ')}`;
        debugDiv.appendChild(message);
        debugDiv.scrollTop = debugDiv.scrollHeight;
    };

    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;

    console.log = (...args: unknown[]) => {
        originalLog.apply(console, args);
        addMessage('log', ...args);
    };

    console.error = (...args: unknown[]) => {
        originalError.apply(console, args);
        addMessage('error', ...args);
    };

    console.warn = (...args: unknown[]) => {
        originalWarn.apply(console, args);
        addMessage('warn', ...args);
    };
}

document.addEventListener('DOMContentLoaded', () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isDebug = urlParams.has('debug');

    if (isDebug) {
        setupDebugConsole();
    }

    // Handle back button - only show if browser history is available
    const backBtn = document.getElementById('pano-back-btn');
    if (backBtn && window.history.length > 1) {
        backBtn.classList.remove('hidden');
        backBtn.addEventListener('click', () => {
            window.history.back();
        });
    }

    const pano = document.getElementById('panorama') as PanOh;

    if (isDebug) {
        pano.setAttribute('debug', '');
    }

    const context: PageContext = JSON.parse(
        document.body.dataset.context ?? '{}'
    );
    const { availableSrc } = context.page;

    // Parse available sources
    const sources = (availableSrc ?? '').trim().split(' ').filter(Boolean);
    const sortedSources = sources
        .map((src) => {
            const match = src.match(/(\d+)k$/);
            const resolution = match ? parseInt(match[1]) * 1024 : 0;
            return { src, resolution };
        })
        .sort((a, b) => b.resolution - a.resolution);

    // Check WebGL capabilities and device specs
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    const supportsFloatTextures = gl?.getExtension('OES_texture_float') !== null;
    const maxTextureSize = gl?.getParameter(gl.MAX_TEXTURE_SIZE) ?? 0;

    // Detect low-end device using MAX_TEXTURE_SIZE as primary indicator:
    // - MAX_TEXTURE_SIZE >= 16384: High-end device (iPhone 15 Pro, flagship Android, desktop) → allow 16k
    // - MAX_TEXTURE_SIZE == 8192: Mid-range device → allow 8k
    // - MAX_TEXTURE_SIZE <= 4096: Low-end device → cap at 8k
    // Also require float texture support for tile system
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isLowEnd = !supportsFloatTextures || maxTextureSize < 16384;

    // Cap at 8k for low-end devices (tile system needs both float textures AND enough RAM/GPU)
    const maxResolution = isLowEnd ? 8192 : Infinity;

    const bestSrc = sortedSources.find(s => s.resolution <= maxResolution)?.src || sortedSources[0]?.src;
    // Find 2k for fast preview (or lowest resolution available)
    const previewSrc = sortedSources.find(s => s.resolution <= 2048)?.src || sortedSources[sortedSources.length - 1]?.src;

    if (isDebug) {
        console.log(`[pan-oh] Device: ${isMobile ? 'mobile' : 'desktop'}, MAX_TEXTURE_SIZE: ${maxTextureSize}`);
        console.log(`[pan-oh] WebGL float textures: ${supportsFloatTextures ? 'supported' : 'NOT supported'}`);
        console.log(`[pan-oh] Low-end device: ${isLowEnd} (${isLowEnd ? 'capping at 8k' : 'allowing 16k'})`);
        console.log(`[pan-oh] Preview source: ${previewSrc}`);
        console.log(`[pan-oh] Best source: ${bestSrc}`);
    }

    // Load 2k preview first for fast feedback
    if (previewSrc) {
        pano.setAttribute('src', previewSrc);

        // Set angle bounds from server context
        const { lonMin, lonMax, latMin, latMax, cropLeft, cropRight, cropTop, cropBottom, backgroundDarken, backgroundDesaturate } = context.page;
        if (lonMin !== undefined) pano.setAttribute('lon-min', lonMin.toString());
        if (lonMax !== undefined) pano.setAttribute('lon-max', lonMax.toString());
        if (latMin !== undefined) pano.setAttribute('lat-min', latMin.toString());
        if (latMax !== undefined) pano.setAttribute('lat-max', latMax.toString());
        if (cropLeft !== undefined) pano.setAttribute('crop-left', cropLeft.toString());
        if (cropRight !== undefined) pano.setAttribute('crop-right', cropRight.toString());
        if (cropTop !== undefined) pano.setAttribute('crop-top', cropTop.toString());
        if (cropBottom !== undefined) pano.setAttribute('crop-bottom', cropBottom.toString());
        if (backgroundDarken !== undefined) pano.setAttribute('background-darken', backgroundDarken.toString());
        if (backgroundDesaturate !== undefined) pano.setAttribute('background-desaturate', backgroundDesaturate.toString());

        if (lonMin !== undefined && lonMax !== undefined) {
            const initialYaw = (lonMin + lonMax) / 2 + 90;
            pano.setAttribute('yaw', initialYaw.toString());
        }
    }

    // Landing animation after preview loads
    pano.addEventListener(
        'textureLoaded',
        async (): Promise<void> => {
            document.getElementById('circular-loader')?.remove();
            await animatePanorama(pano, pano.yaw, 0, 2, 2000);

            // After animation completes, upgrade to best resolution if different
            if (bestSrc && bestSrc !== previewSrc) {
                if (isDebug) {
                    console.log(`[pan-oh] Upgrading to: ${bestSrc}`);
                }
                pano.setAttribute('src', bestSrc);
            }
        },
        { once: true }
    );

    // Handle load errors
    pano.addEventListener(
        'loadError',
        (): void => {
            document.getElementById('circular-loader')?.remove();
            console.error('[pan-oh] Failed to load panorama');
        },
        { once: true }
    );

    // Takeoff animation
    const mapButton = document.getElementById('map-button');
    if (mapButton) {
        mapButton.addEventListener('click', async (e) => {
            e.preventDefault();

            const href = mapButton.getAttribute('href');
            if (!href) return;

            await animatePanorama(pano, pano.yaw, -89, 0.525, 1200);
            window.location.href = href;
        });
    }
});
